import React, { useContext, useEffect, useRef } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  SearchContext,
  AutherContext,
  KBContext,
} from "../../Context";
import ArticleSearch from "../../Components/ArticleSearch/ArticleSearch";
import LegacyArticleSearch from "../../Components/Legacy/LegacyArticleSearch";
import Layout from "../../Components/Layout";
import LegacyLayout from "../../Components/Legacy/LegacyLayout";
import { CategoryLoader } from "../../Components/Loaders/CategoryLoader";
import { NewCategoryLoader } from "../../Components/Loaders/NewCategoryLoader";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch";
import CategoryList from "./CategoryList";
import { useSubCategoriesFeature } from "../../Context/KBContext/hooks/useFeatures";
import { isFeatureEnabled } from "../../utils";
import { FTR_KB_NEW_UI } from "../../Constants";

/* New UI: no margin so content aligns with search bar in Layout's column */
const ArticleWrapper = styled.section`
  display: flex;
  box-sizing: border-box;
  margin: 0;
  margin-top: 0;
  flex-direction: row;
  align-content: center;
  justify-content: center;
  width: 100%;
`;

/* Legacy UI: responsive margins matching old layout */
const LegacyArticleWrapper = styled.section`
  display: flex;
  box-sizing: border-box;
  margin: 0 180px;
  margin-top: 48px;

  align-content: center;
  justify-content: center;
  flex-direction: row;

  @media screen and (max-width: 1100px) {
    margin: 0 90px;
    margin-top: 28px;
  }

  @media screen and (max-width: 800px) {
    margin: 0 50px;
    margin-top: 18px;
  }

  @media screen and (max-width: 500px) {
    margin: 0 24px;
    margin-top: 32px;
  }

  transition: margin 1s;
`;

function isArticleDetailPath(pathname) {
  const segments = pathname.replace(/^\//, "").split("/").filter(Boolean);
  return segments.length >= 2;
}

export default function Article() {
  const { searchList, searchInput } = useContext(SearchContext);
  const ref = useRef();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { data, availableFeatures } = useContext(KBContext);
  const { isInternal } = data;
  const { redirectToken, hasPermission } = useContext(AutherContext);
  const { isSubCategoriesFeatureEnabled } = useSubCategoriesFeature();

  const isNewUI = isFeatureEnabled(FTR_KB_NEW_UI, availableFeatures);
  const showCategoryList = isNewUI
    ? !isSubCategoriesFeatureEnabled && !isArticleDetailPath(pathname)
    : !isSubCategoriesFeatureEnabled;

  const LayoutComponent = isNewUI ? Layout : LegacyLayout;
  const WrapperComponent = isNewUI ? ArticleWrapper : LegacyArticleWrapper;

  const showArticle = () => {
    if (isInternal !== undefined && !isInternal) {
      return true;
    } else {
      return hasPermission;
    }
  };

  const navigate = useNavigateWithSearch();

  useEffect(() => {
    if (!redirectToken && isInternal && !hasPermission) {
      localStorage.clear();
      navigate("/login");
    }
  }, [isInternal, hasPermission, redirectToken, navigate]);

  function focus() {
    ref?.current?.focus();
  }

  const ArticleSearchComponent = isNewUI ? ArticleSearch : LegacyArticleSearch;

  return showArticle() ? (
    <LayoutComponent>
      {searchInput?.length > 0 && searchList ? (
        <ArticleSearchComponent />
      ) : (
        <WrapperComponent ref={ref}>
          {showCategoryList ? <CategoryList focus={focus} /> : null}
          <Outlet />
        </WrapperComponent>
      )}
    </LayoutComponent>
  ) : (
    isNewUI ? <NewCategoryLoader /> : <CategoryLoader />
  );
}
